import { ActionsCell, GrantsCell, SpecialAuthorizationCell, TextCell } from './permissions-cells.component'
import {
  useCurrentProject,
  WithPermission,
} from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { ProjectService } from 'src/services/project.service'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CurecallButton,
  CurecallConfirmationModal,
  CurecallList,
  CurecallListHeaders,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { EPermissionGrantActions, EPermissionGrantSubjects, TPermission } from './permissions.type'
import {
  EList,
  getListPreferedOrder,
  setListPreferedOrder,
  TOrderBy,
} from 'src/utils/list-order-preference.util'

type TPermissionHeaderName =
  | 'Id'
  | 'Name'
  | 'Grants'
  | 'SpecialAuthorizations'
  | 'IpAddressWhiteList'
  | 'Actions'

type TTableRow = { [key in TPermissionHeaderName]: ReactNode }

type TSortableColumn = Extract<TPermissionHeaderName, 'Id'>
const COLUMN_SORTS: { [key in TSortableColumn]: any } = {
  Id: (permissions: TPermission[]) => [...permissions].sort((p1, p2) => p1.id - p2.id),
}

export default function PermissionList({
  permissions,
}: Readonly<{
  permissions: TPermission[]
}>) {
  const defaultPreferedOrder: TOrderBy<TSortableColumn> = getListPreferedOrder(EList.PERMISSION_LIST) || {
    column: 'Id',
    descOrder: false,
  }

  const defaultSortedPermissions = defaultPreferedOrder.descOrder
    ? COLUMN_SORTS[defaultPreferedOrder.column as TSortableColumn](permissions).reverse()
    : COLUMN_SORTS[defaultPreferedOrder.column as TSortableColumn](permissions)

  return (
    <ProgrammedMessageListComponent
      defaultPreferedOrder={defaultPreferedOrder}
      defaultSortedPermissions={defaultSortedPermissions}
    />
  )
}

function ProgrammedMessageListComponent({
  defaultPreferedOrder,
  defaultSortedPermissions,
}: Readonly<{
  defaultPreferedOrder: TOrderBy<TSortableColumn>
  defaultSortedPermissions: TPermission[]
}>) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { pathname } = useLocation()
  const { project, currentPermission } = useCurrentProject()
  const queryClient = useQueryClient()
  const { notify } = useNotification()

  const [currentOrder, setCurrentOrder] = useState<TOrderBy<'Id'>>(defaultPreferedOrder)
  const [permissionsSorted, setPermissionsSorted] = useState<Array<TPermission>>(defaultSortedPermissions)
  const [permissionToDelete, setPermissionToDelete] = useState<TPermission | null>(null)

  const deletePermission = useMutation({
    mutationFn: (permissionId: number) => {
      return ProjectService.deletePermission(project.id, permissionId)
    },
    onError: () => {
      notify(
        {
          title: t('pages.configuration.permissions.error-title'),
          message: t('pages.configuration.permissions.error-body'),
          appearance: 'ERROR',
        },
        15000,
      )
    },
    onSuccess: (deletedPermission) => {
      queryClient.setQueryData(['permissions', project.id], (oldData: any) => {
        if (!oldData) return
        const newData = oldData.filter((permission: TPermission) => permission.id !== deletedPermission.id)
        setPermissionsSorted(newData)
        return newData
      })
      notify(
        {
          title: t('pages.configuration.permissions.success-title'),
          message: t('pages.configuration.permissions.success-body'),
          appearance: 'SUCCESS',
        },
        15000,
      )
    },
  })

  const confirmDeletePermission = async (permissionId: number) => {
    setPermissionToDelete(null)
    deletePermission.mutate(permissionId)
  }

  const sortTableByColumn = (columnToSort: TSortableColumn) => {
    const shouldReverseOrder = currentOrder.column === columnToSort
    const newOrder = {
      column: columnToSort,
      descOrder: shouldReverseOrder ? !currentOrder.descOrder : false,
    }

    const colonneSort = COLUMN_SORTS[columnToSort]
    setPermissionsSorted((prev) => (newOrder.descOrder ? colonneSort(prev).reverse() : colonneSort(prev)))
    setCurrentOrder(newOrder)
    setListPreferedOrder(EList.PERMISSION_LIST, newOrder)
  }

  const tableRows: Array<TTableRow> =
    permissionsSorted?.map((permission: TPermission) => ({
      Id: <TextCell>{permission.id}</TextCell>,
      Name: <TextCell>{permission.name}</TextCell>,
      SpecialAuthorizations: (
        <SpecialAuthorizationCell specialAuthorizations={permission.specialAuthorizations} />
      ),
      IpAddressWhiteList: <TextCell>{permission.ipAddressWhiteList?.join(', ')}</TextCell>,
      Grants: <GrantsCell grants={permission.grants} />,
      Actions: (
        <ActionsCell
          permission={permission}
          onDelete={() => setPermissionToDelete(permission)}
          disableDelete={currentPermission.id === permission.id}
          onUpdate={(permissionId: number) => navigate(`${pathname}/${permissionId}`)}
        />
      ),
    })) ?? []

  const columnsData: CurecallListHeaders<TTableRow> = [
    {
      label: t('pages.configuration.permissions.id'),
      key: 'Id',
      isSorted: true,
      onHeaderClick: () => sortTableByColumn('Id'),
    },
    { label: t('pages.configuration.permissions.name'), key: 'Name' },
    { label: t('pages.configuration.permissions.special-authorizations'), key: 'SpecialAuthorizations' },
    { label: t('pages.configuration.permissions.ip-white'), key: 'IpAddressWhiteList' },
    { label: t('pages.configuration.permissions.grants'), key: 'Grants' },
    { label: t('pages.configuration.permissions.actions'), key: 'Actions' },
  ]

  return (
    <div className="c-flex c-flex-col c-max-h-full">
      <header className="c-flex c-mb-6 c-items-end">
        <WithPermission
          requiredSubject={EPermissionGrantSubjects.USER_MANAGEMENT}
          requiredAction={EPermissionGrantActions.CREATE}
        >
          <CurecallButton
            appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
            size={ECurecallSize.SMALL}
            onClick={() => navigate(`/${project.id}/configuration/permissions/add`)}
            className="c-ml-auto"
          >
            {t('pages.configuration.permissions.create-permission')}
          </CurecallButton>
        </WithPermission>
      </header>

      <CurecallList
        rowsData={tableRows}
        defaultSortData={{ key: 'Id', descOrder: false }}
        headersData={columnsData}
        currentOrder={{ key: currentOrder.column, descOrder: currentOrder.descOrder }}
      />

      {permissionToDelete && (
        <CurecallConfirmationModal
          color="ALERT"
          title={t('pages.configuration.permissions.confirm-delete')}
          isOpen={!!permissionToDelete}
          cancelButtonLabel={t('common.actions.cancel')}
          confirmButtonLabel={t('common.actions.confirm')}
          onCancel={() => setPermissionToDelete(null)}
          onConfirm={() => confirmDeletePermission(permissionToDelete.id)}
        >
          {permissionToDelete.name}
        </CurecallConfirmationModal>
      )}
    </div>
  )
}

export { PermissionList }
